/**
 * CrossPortalCompletion Component for Ufficio Stile Portal
 *
 * Provides "Completa e Notifica" functionality for FASE 6 workflow.
 * Allows completion of requests with automatic notification to other portals.
 *
 * NOTA: i portali (Personalizzati, Configuratore) vengono SEMPRE notificati al
 * completamento. La gestione manuale dei flag è stata rimossa dall'interfaccia
 * perché il completamento deve sempre notificarli.
 */

import React from 'react';
import {
  Button,
  Modal,
  Stack,
  Text,
  Textarea,
  Group,
  Alert,
  Badge,
  Checkbox,
  Divider
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { DatePickerInput } from '@mantine/dates';
import {
  IconSend,
  IconInfoCircle,
  IconCalendar
} from '@tabler/icons-react';
import { useCrossPortal } from '../../hooks/use-cross-portal';
import { Request } from '../../types/api';
import { toDateOnlyISOString } from '../../utils/date-utils';

// I portali da notificare sono fissi: il completamento li notifica sempre entrambi.
const NOTIFY_PORTALS = ['Personalizzati', 'Configuratore'];

interface CrossPortalCompletionProps {
  request: Request;
  opened: boolean;
  onClose: () => void;
  onCompleted?: () => void;
  userId: string;
  userName?: string;
}

export function CrossPortalCompletion({
  request,
  opened,
  onClose,
  onCompleted,
  userId,
  userName
}: CrossPortalCompletionProps) {
  const { completeAndNotify, isCompleting } = useCrossPortal({
    onCompleteSuccess: () => {
      onCompleted?.();
      onClose();
      form.reset();
    },
    onCompleteError: (error) => {
      console.error('Cross-portal completion error:', error);
    }
  });

  const form = useForm({
    initialValues: {
      completionNotes: '',
      confirmCompletion: false,
      closedDate: new Date() as Date | null
    },
    validate: {
      // Le note di completamento sono facoltative.
      confirmCompletion: (value) => {
        if (!value) {
          return 'È necessario confermare il completamento';
        }
        return null;
      }
    }
  });

  const handleSubmit = async (values: typeof form.values) => {
    try {
      await completeAndNotify({
        requestId: request.id,
        completionNotes: values.completionNotes,
        userId,
        userName: userName || 'Utente Ufficio Stile',
        userRole: 'UfficioStile',
        notifyPortals: NOTIFY_PORTALS,
        completedAt: values.closedDate ? toDateOnlyISOString(values.closedDate) : undefined
      });
    } catch (error) {
      console.error('Error completing request:', error);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Completa e Notifica"
      size="md"
      centered
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <Alert color="blue" variant="light">
            <Group>
              <IconInfoCircle size="1rem" />
              <Text size="sm">
                Questa operazione completerà la richiesta e invierà la notifica di completamento ai portali collegati.
              </Text>
            </Group>
          </Alert>

          {/* Request Info */}
          <Stack gap="xs">
            <Text fw={500} size="sm">Richiesta da Completare</Text>
            <Group>
              <Badge variant="light" color="blue">
                {request.requestType}
              </Badge>
              <Text size="sm" c="dimmed">
                {request.clientName}
              </Text>
            </Group>
          </Stack>

          <Divider />

          {/* Completion Notes (facoltative) */}
          <Textarea
            label="Note di Completamento"
            placeholder="Descrivi i dettagli del completamento della richiesta..."
            minRows={4}
            maxRows={8}
            description="Facoltative"
            {...form.getInputProps('completionNotes')}
          />

          {/* Data di Chiusura */}
          <DatePickerInput
            label="Data di Chiusura"
            placeholder="Seleziona data"
            leftSection={<IconCalendar size={16} />}
            valueFormat="DD/MM/YYYY"
            locale="it"
            required
            {...form.getInputProps('closedDate')}
          />

          <Divider />

          {/* Confirmation */}
          <Checkbox
            label="Confermo il completamento della richiesta"
            description="Una volta completata, la richiesta non potrà essere modificata senza riapertura"
            color="orange"
            {...form.getInputProps('confirmCompletion')}
          />

          {/* Actions */}
          <Group justify="flex-end" mt="md">
            <Button
              variant="light"
              onClick={handleClose}
              disabled={isCompleting}
            >
              Annulla
            </Button>
            <Button
              type="submit"
              loading={isCompleting}
              leftSection={<IconSend size="1rem" />}
            >
              Completa e Notifica
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

export default CrossPortalCompletion;
